import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquare, Bell, Loader2, CheckCircle, 
  AlertCircle, Clock, Send, Smartphone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SMSSettings = () => {
  const { user } = useAuth();
  const [smsStatus, setSmsStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState({
    game_reminders: true,
    score_updates: true,
    schedule_changes: true
  });

  useEffect(() => {
    fetchSMSStatus();
    if (user?.role === 'admin') {
      fetchLogs();
    }
  }, [user]);

  const fetchSMSStatus = async () => {
    try {
      const res = await axios.get(`${API}/sms/status`);
      setSmsStatus(res.data);
    } catch (error) {
      console.error('Error fetching SMS status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/sms/logs?limit=20`);
      setLogs(res.data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-muted-foreground">Please login to manage SMS settings</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="sms-settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <MessageSquare className="h-8 w-8" />
          SMS Notifications
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage text message alerts for games and updates
        </p>
      </div>

      {/* Status Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            SMS Service Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : smsStatus ? (
            <div className="flex items-center gap-4">
              <Badge variant={smsStatus.enabled ? 'default' : 'secondary'} className="gap-1">
                {smsStatus.enabled ? (
                  <>
                    <CheckCircle className="h-3 w-3" />
                    Live ({smsStatus.provider === 'pingram' ? 'Pingram' : smsStatus.provider})
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-3 w-3" />
                    Demo Mode
                  </>
                )}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {smsStatus.enabled 
                  ? `Real SMS messages will be sent via ${smsStatus.provider === 'pingram' ? 'Pingram' : smsStatus.provider}`
                  : 'SMS messages are simulated and logged but not sent'
                }
              </span>
            </div>
          ) : (
            <p className="text-muted-foreground">Unable to fetch status</p>
          )}
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Choose which SMS alerts you want to receive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Game Reminders</Label>
              <p className="text-sm text-muted-foreground">
                Receive SMS 24 hours before your games
              </p>
            </div>
            <Switch 
              checked={preferences.game_reminders}
              onCheckedChange={(checked) => setPreferences({...preferences, game_reminders: checked})}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Score Updates</Label>
              <p className="text-sm text-muted-foreground">
                Get notified when game scores are posted
              </p>
            </div>
            <Switch 
              checked={preferences.score_updates}
              onCheckedChange={(checked) => setPreferences({...preferences, score_updates: checked})}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Schedule Changes</Label>
              <p className="text-sm text-muted-foreground">
                Alerts when game times or fields change
              </p>
            </div>
            <Switch 
              checked={preferences.schedule_changes}
              onCheckedChange={(checked) => setPreferences({...preferences, schedule_changes: checked})}
            />
          </div>
          
          <Button className="mt-4" onClick={() => toast.success('Preferences saved')}>
            Save Preferences
          </Button>
        </CardContent>
      </Card>

      {/* Admin: SMS Logs */}
      {user?.role === 'admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent SMS Logs
            </CardTitle>
            <CardDescription>
              View sent and simulated SMS messages
            </CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No SMS messages sent yet
              </p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {logs.map((log, index) => (
                  <div 
                    key={log.id || index}
                    className="p-3 rounded-lg border bg-muted/50"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant={log.status === 'mock' ? 'secondary' : log.status === 'sent' ? 'default' : 'destructive'}>
                        {log.status === 'mock' && 'Simulated'}
                        {log.status === 'sent' && 'Sent'}
                        {log.status === 'failed' && 'Failed'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm font-medium">{log.phone_number}</p>
                    <p className="text-sm text-muted-foreground mt-1">{log.message}</p>
                    <Badge variant="outline" className="mt-2 text-xs">
                      {log.notification_type}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
